import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { useDashboardContext } from '@/common/dashboard/DashboardContext';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useState } from 'react';

const CHART_GREEN = '#1B5E20';

export const ChbkFinTrendChart = ({ totalValues, ctrtTypeIdToCtrtType }) => {
  const { formValues } = useDashboardContext();
  let { dateBasis, monthPeriodId, qtrPeriodId, date, periodicity, distCustIds, ctrtTypeIds, prodIds, agg } = formValues;

  const { totalProdIds, totalDistCustIds } = totalValues;

  const rsrc = useRsrc();

  const { navigateTo } = useNavState();

  const [data, setData] = useState([]);

  const [finalProdIds, setFinalProdIds] = useState([]);
  const [prodIdsNot, setProdIdsNot] = useState(false);
  const [finalDistCustIds, setFinalDistCustIds] = useState([]);
  const [distCustIdsNot, setDistCustIdsNot] = useState(false);

  const chartData =
    data?.series ?
      {
        ...data,
        series: data.series.map((seriesItem) => ({
          ...seriesItem,
          color: CHART_GREEN,
        })),
      }
    : data;

  const formatDayCategory = (value) => {
    const raw = String(value ?? '');
    if (raw.length !== 8) {
      return raw;
    }

    const year = raw.substring(0, 4);
    const month = raw.substring(4, 6);
    const day = raw.substring(6, 8);
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    const periodId =
      periodicity === 'MONTH' ? monthPeriodId
      : periodicity === 'QUARTER' ? qtrPeriodId
      : null;
    if (!periodId && !date) {
      return;
    }

    let prodIdsNot = true;
    if (totalProdIds.length / 2 < prodIds.length) {
      prodIds = totalProdIds.filter((id) => !prodIds.includes(id));
    } else {
      prodIdsNot = false;
    }
    setFinalProdIds(prodIds);
    setProdIdsNot(prodIdsNot);

    let distCustIdsNot = true;
    if (totalDistCustIds.length / 2 < distCustIds.length) {
      distCustIds = totalDistCustIds.filter((id) => !distCustIds.includes(id));
    } else {
      distCustIdsNot = false;
    }
    setFinalDistCustIds(distCustIds);
    setDistCustIdsNot(distCustIdsNot);

    const reqParam = {
      dateBasis,
      periodId,
      date,
      periodicity,
      distCustIds,
      distCustIdsNot,
      prodIds,
      prodIdsNot,
      ctrtTypes: ctrtTypeIds.map((id) => ctrtTypeIdToCtrtType[id]),
      agg,
    };

    rsrc.data('sc', 'CHBK_FIN_TREND_CHART_DATA_PLUGIN', reqParam).then((res) => {
      setData(res);
    });
  }, [dateBasis, monthPeriodId, qtrPeriodId, date, periodicity, distCustIds, ctrtTypeIds, prodIds, agg]);

  const navigateToLedgerLineList = (dataPoint) => {
    const category = dataPoint.category;
    const rowsReq = {};
    if (periodicity === 'MONTH') {
      rowsReq.expr = ExprUtils.whereParams('code', category);
    } else if (periodicity === 'QUARTER') {
      const year = category.substring(0, 4);
      const quarter = category.substring(5);
      const monthStart = (parseInt(quarter) - 1) * 3 + 1;
      const monthPeriodCodes = [monthStart, monthStart + 1, monthStart + 2].map(
        (month) => year + 'M' + month.toString().padStart(2, '0'),
      );
      rowsReq.expr = ExprUtils.whereParams('code', monthPeriodCodes);
    } else if (periodicity === 'DAY') {
      const monthPeriodCode = category.substring(0, 4) + 'M' + category.substring(4, 6);
      rowsReq.expr = ExprUtils.whereParams('code', monthPeriodCode);
    }
    rsrc.rows('sc', 'PERIOD', rowsReq).then((periods) => {
      const periodIds = periods.map((period) => period.id);
      let expr = ExprUtils.whereParams(dateBasis === 'PYMT_DATE' ? 'pymtPeriodId' : 'invPeriodId', periodIds);

      if (finalProdIds.length > 0) {
        let prodExpr;
        if (prodIdsNot) {
          prodExpr = ExprUtils.isNotOneOf('prodId', finalProdIds);
        } else {
          prodExpr = ExprUtils.whereParams('prodId', finalProdIds);
        }
        expr = ExprUtils.and(expr, prodExpr);
      }

      if (periodicity === 'DAY') {
        const dateExpr = ExprUtils.whereParams(dateBasis === 'PYMT_DATE' ? 'pymtDate' : 'invDate', parseInt(category));
        expr = ExprUtils.and(expr, dateExpr);
      }

      if (finalDistCustIds.length > 0) {
        let distCustExpr;
        if (distCustIdsNot) {
          distCustExpr = ExprUtils.isNotOneOf('distCustId', finalDistCustIds);
        } else {
          distCustExpr = ExprUtils.whereParams('distCustId', finalDistCustIds);
        }
        expr = ExprUtils.and(expr, distCustExpr);
      }

      // const ctrtExpr = ExprUtils.whereParams('type', ctrtTypeIds.map(id => ctrtTypeIdToCtrtType[id]));

      // expr = ExprUtils.and(expr, ctrtExpr);

      const navState = {
        expr: expr,
      };
      navigateTo('/sc/trx-viewer/TRX_IS', navState);
    });
  };

  return (
    <>
      <CartesianChart
        data={chartData}
        yLabel={
          agg === 'PAID_AMT' ? 'Paid Amount'
          : agg === 'NET_AMT' ?
            'Net Amount'
          : 'Paid Quantity'
        }
        xLabel={(dateBasis === 'PYMT_DATE' ? 'Payment' : 'Invoice') + (periodicity === 'DAY' ? ' Date' : ' Period')}
        xTickFormatter={periodicity === 'DAY' ? formatDayCategory : undefined}
        allowNegative
        onClick={(dataPoint) => navigateToLedgerLineList(dataPoint)}
      />
    </>
  );
};
